const toNumber = (value) => {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

export const STATUS_CONFIG = {
  FILLED: { label: 'Filled', tone: 'success' },
  SUBMITTED: { label: 'Submitted', tone: 'info' },
  PENDING: { label: 'Pending', tone: 'warning' },
  CANCELLED: { label: 'Cancelled', tone: 'neutral' },
  REJECTED: { label: 'Rejected', tone: 'danger' },
  PARTIALLY_FILLED: { label: 'Partial', tone: 'info' },
  EXPIRED: { label: 'Expired', tone: 'neutral' },
  FAILED: { label: 'Failed', tone: 'danger' },
};

export const CANCELLABLE_STATUSES = new Set(['SUBMITTED', 'PENDING']);
export const REJECT_REASON_STATUSES = new Set(['REJECTED', 'FAILED']);
export const REJECT_REASON_EMPTY_LABEL = 'No reason provided';

export const SIDE_OPTIONS = [
  { value: '', label: 'All sides' },
  { value: 'BUY', label: 'Buy' },
  { value: 'SELL', label: 'Sell' },
];

export const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'SUBMITTED', label: 'Submitted' },
  { value: 'PARTIALLY_FILLED', label: 'Partially filled' },
  { value: 'FILLED', label: 'Filled' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'EXPIRED', label: 'Expired' },
  { value: 'FAILED', label: 'Failed' },
];

export const statusConfig = (status) =>
  STATUS_CONFIG[status] || { label: status || 'Unknown', tone: 'neutral' };

export const hasRejectReasonText = (rejectReason) =>
  typeof rejectReason === 'string' && rejectReason.trim().length > 0;

export const orderShowsRejectReason = (status) => REJECT_REASON_STATUSES.has(status);

export const fmtQty = (value) => {
  const n = toNumber(value);
  if (n == null) return '—';
  return n.toLocaleString('en-US', { maximumFractionDigits: 8 });
};

export const fmtPrice = (value) => {
  const n = toNumber(value);
  if (n == null) return '—';
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 });
};

export const fmtDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const textOrNull = (value) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
};

export const orderCustomer = (order) => (
  order?.customer && typeof order.customer === 'object' && !Array.isArray(order.customer)
    ? order.customer
    : null
);

export const customerDisplayName = (customer) => textOrNull(customer?.displayName);

export const customerInitials = (customer, customerId) => {
  const name = customerDisplayName(customer);
  if (name) {
    const parts = name.split(/\s+/).slice(0, 2);
    return parts.map((part) => part.charAt(0).toUpperCase()).join('');
  }
  const source = textOrNull(customer?.email) || customerId || '';
  return source.replace(/[^a-z0-9]/gi, '').slice(0, 2).toUpperCase() || '??';
};

export const filledQuantity = (order) => toNumber(order?.filledQuantity);

export const averagePrice = (order) => toNumber(order?.averageFillPrice);

export const limitPrice = (order) => toNumber(order?.limitPrice);

export const instrumentName = (order) => textOrNull(order?.instrument?.name);

export const orderCurrency = (order) => textOrNull(order?.currencyCode);

export const orderExpiration = (order) => {
  const value = textOrNull(order?.expiration);
  return value === 'DAY' || value === 'GTC' ? value : null;
};

export const orderMarket = (order) => textOrNull(order?.marketSlug);

export const orderExternalId = (order) => textOrNull(order?.externalId);

export const ORDER_TYPE_LABELS = {
  MARKET: 'Market',
  LIMIT: 'Limit',
};

export const EXPIRATION_LABELS = {
  DAY: 'Day',
  GTC: 'Good till cancelled',
};

const AVATAR_TONES = [
  'bg-accent-100 text-accent-700',
  'bg-success-100 text-success-700',
  'bg-info-100 text-info-700',
  'bg-warning-100 text-warning-800',
  'bg-danger-100 text-danger-700',
];

export const orderTypeLabel = (type) => ORDER_TYPE_LABELS[type] || textOrNull(type);

export const expirationLabel = (order) => {
  const code = orderExpiration(order);
  return code ? EXPIRATION_LABELS[code] || code : null;
};

export const remainingQuantity = (order) => {
  const quantity = toNumber(order?.quantity);
  const filled = filledQuantity(order);
  if (quantity == null || filled == null) return null;
  const remaining = quantity - filled;
  return Number.isFinite(remaining) ? remaining : null;
};

export const fillRatio = (order) => {
  const quantity = toNumber(order?.quantity);
  const filled = filledQuantity(order);
  if (quantity == null || filled == null || quantity <= 0) return null;
  return Math.min(1, Math.max(0, filled / quantity));
};

export const executedNotional = (order) => {
  const filled = filledQuantity(order);
  const average = averagePrice(order);
  if (filled == null || average == null) return null;
  const notional = filled * average;
  return Number.isFinite(notional) ? notional : null;
};

export const instrumentMarket = (order) =>
  textOrNull(order?.instrument?.marketSlug) || orderMarket(order);

export const customerSubtitle = (order) => {
  const customer = orderCustomer(order);
  const named = customerDisplayName(customer);
  return textOrNull(customer?.email)
    || textOrNull(customer?.referenceCode)
    || textOrNull(customer?.externalReference)
    || (named ? textOrNull(order?.customerId) : null);
};

export const avatarToneClass = (id) => {
  const key = textOrNull(typeof id === 'string' ? id : null) || '';
  if (!key) return AVATAR_TONES[0];
  return AVATAR_TONES[key.charCodeAt(0) % AVATAR_TONES.length];
};

export const fmtDateParts = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return {
    date: date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
    time: date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
  };
};

export const sideLabel = (side) => {
  if (side === 'BUY') return 'Buy';
  if (side === 'SELL') return 'Sell';
  return textOrNull(side);
};

export const fmtMoney = (value, currency) => {
  const amount = fmtPrice(value);
  if (amount === '—') return amount;
  return currency ? `${amount} ${currency}` : amount;
};

/** A real limit when the gateway sent one; MKT only for a market order with no limit. */
export const limitQuoteLabel = (order) => {
  const limit = limitPrice(order);
  if (limit != null) return fmtMoney(limit, orderCurrency(order));
  if (order?.type === 'MARKET') return 'MKT';
  return '—';
};

export const orderMatchesQuery = (order, query) => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const customer = orderCustomer(order);
  const haystack = [
    order?.id,
    order?.externalId,
    order?.symbol,
    order?.assetId,
    order?.marketSlug,
    order?.customerId,
    customerDisplayName(customer),
    customer?.email,
    customer?.referenceCode,
    customer?.externalReference,
    instrumentName(order),
  ];
  return haystack.some((value) => typeof value === 'string' && value.toLowerCase().includes(q));
};
